/**
 * 시뮬레이션 최종 보고서 화면의 상세 chrome(헤더 좌/우 + 하단 액션바) 조립.
 *
 * ⚠️ web 배포 상세(apps/web/src/pages/reports/hooks/useComposedReportActions.tsx)의
 * **DRAFT 직후(서명 0·이력 0·작성자 시점)** 분기와 동형이어야 한다(시뮬=배포판 충실도 제1원칙).
 * 노출/순서/슬롯/라벨 해소는 rc 공유 경로(resolveDetailChromeActions + buildApprovalActions/
 * buildChromeActions — web 상세와 같은 코드)가 소유하므로, 이 파일의 드리프트 표면은
 * "어떤 액션을 태깅하나(가시성 게이트)"와 guard의 enable 단순화뿐이다. web 쪽 태깅
 * 게이트가 바뀌면 여기도 같이 고칠 것.
 *
 * enable 단순화(2026-07-26 사용자 확정 — 표시에 영향 주는 것만 DRAFT 직후 기본 상태로):
 * - 시뮬 작성자 = report 작성자(author 시점) → edit/delete/approvalLineEdit 노출.
 * - 결재 이력 0 → approvalHistory 미태깅(web hasApprovalHistory=false 동형).
 * - 서명 0 → contactSignatureComplete=false, authorSigned=false.
 * - 제출 후 흐름 재진입(`postSubmitSetup`) **미태깅** — 그 버튼은 *설정을 마친 뒤* 재진입 전용인데
 *   여기는 DRAFT 직후(설정 전)라 web에서도 뜨지 않는다(`isSetupComplete` 게이트). 이 시뮬이
 *   재는 시점이 바뀌면(설정 후 상태를 담게 되면) 그때 함께 태깅할 것.
 * - 제출 후 흐름의 활동 화면(`postSubmitActivity`) **미태깅** — 같은 이유의 한 걸음 앞이다.
 *   그 흐름의 활동은 설정을 마쳐야 **시작될 수 있고**(TBM 교육이면 배포 설정이 교육 기간·담당자를
 *   정한다), 여기는 그 전 시점이라 갈 화면 자체가 아직 없다. 위와 같은 조건에서 함께 태깅할 것.
 * - 버튼 동작은 전부 host 주입 onAction(no-op + 토스트) — 뒤로가기만 시뮬 동선 매핑(호출부).
 */
import {
  buildApprovalActions,
  buildApprovalLineEditAction,
  buildDeleteAction,
  buildEditAction,
  PenIcon,
  resolveDetailChromeActions,
} from '@safetyhub/report-composer';
import type {
  ApprovalGuardResult,
  DetailChromeResolved,
  TaggedDetailAction,
} from '@safetyhub/report-composer';
import { DETAIL_CHROME_ACTION_LABELS } from '@safetyhub/shared';
import type { DetailChromeConfig } from '@safetyhub/shared';

export interface SimulationDetailChromeArgs {
  /** 템플릿(스냅샷) detailChrome — 상태별 표시 오버라이드. 미지정=레지스트리 기본 배치. */
  detailChrome: DetailChromeConfig | undefined;
  /**
   * 템플릿 결재 정책 유무 — approvalLineMeta.defaultApprovalLine 단계 존재로 파생
   * (서버 resolveComposedApprovalMeta의 hasApprovalLinePolicy 동형: 템플릿 단독 권위).
   */
  hasApprovalLinePolicy: boolean;
  /** dry-run 결과 결재라인 단계 수(approvalLineDefinition 행 수) — 0이면 web noApprovalLines 동형. */
  approvalLineCount: number;
  /** 점검 컨텍스트 존재 여부 — contactSign은 점검 컨텍스트(inspectionId) 필수(web 동형). */
  hasInspectionContext: boolean;
  /** 하단바 가용 폭(px) — web useViewportWidth 동형(fit 기반 오버플로우). */
  availableWidth: number;
  /** 모든 버튼 동작(no-op + 토스트) — 시뮬에서는 실제 동작하지 않는다. */
  onAction: () => void;
}

/**
 * DRAFT 직후 상태의 배포판 상세 chrome을 3슬롯(StickyHeaderAction[])으로 해소한다.
 * web ComposerReportDetail의 DRAFT 직후와 같은 버튼 배치·라벨(동작만 no-op).
 */
export function resolveSimulationDetailChrome(
  args: SimulationDetailChromeArgs,
): DetailChromeResolved {
  const {
    detailChrome,
    hasApprovalLinePolicy,
    approvalLineCount,
    hasInspectionContext,
    availableWidth,
    onAction,
  } = args;

  const tagged: TaggedDetailAction[] = [];

  // ── 헤더 좌측: 결재 이력 — 이력 0(DRAFT 직후)이라 미태깅(web hasApprovalHistory 게이트 동형). ──

  // ── 헤더 우측: 결재라인 편집(정책 활성 + DRAFT + 작성자) + 수정 + 삭제(작성자 canShow). ──
  if (hasApprovalLinePolicy) {
    tagged.push({
      actionId: 'approvalLineEdit',
      defaultSlot: 'headerRight',
      mandatory: false,
      action: buildApprovalLineEditAction(onAction),
    });
  }
  tagged.push({
    actionId: 'delete',
    defaultSlot: 'headerRight',
    mandatory: false,
    action: buildDeleteAction({ onClick: onAction }),
  });
  tagged.push({
    actionId: 'edit',
    defaultSlot: 'headerRight',
    mandatory: false,
    action: buildEditAction(onAction),
  });

  // ── 현장 담당자 서명: 템플릿 opt-in(detailChrome 명시) + 점검 컨텍스트(web 게이트 동형). ──
  const contactSignOptedIn = !!detailChrome?.items?.some((i) => i.actionId === 'contactSign');
  if (contactSignOptedIn && hasInspectionContext) {
    tagged.push({
      actionId: 'contactSign',
      defaultSlot: 'actionBar',
      mandatory: false,
      action: {
        key: 'contact-sign',
        label: DETAIL_CHROME_ACTION_LABELS.contactSign,
        onClick: onAction,
        // 서명 0(DRAFT 직후) → primary(web contactSignatures.length >= 1 ? secondary : primary).
        variant: 'primary',
        icon: <PenIcon className="h-4 w-4" />,
      },
    });
  }

  // ── 라이프사이클(mandatory): 결재 ▼(정책 활성) / 완료(정책 없음) — web approvalGuard의
  //    DRAFT 분기를 서명 0·비진행 상태로 단순화(라벨·드롭다운 구성은 buildApprovalActions 공유). ──
  const guard: ApprovalGuardResult = hasApprovalLinePolicy
    ? {
        mode: 'approval',
        hidden: false,
        submit: {
          enabled: approvalLineCount > 0,
          reason: approvalLineCount > 0 ? undefined : '결재라인을 먼저 설정해주세요',
          busy: false,
          onClick: onAction,
        },
        cancel: { enabled: false, reason: '결재 진행 중이 아닙니다', onClick: onAction },
        resend: { enabled: false, reason: '결재 진행 중이 아닙니다', onClick: onAction },
      }
    : {
        mode: 'completion',
        hidden: false,
        submit: { enabled: true, busy: false, onClick: onAction },
        cancel: null,
        resend: null,
      };
  const lifecycleAction = buildApprovalActions(guard)[0];
  if (lifecycleAction) {
    tagged.push({
      actionId: hasApprovalLinePolicy ? 'submit' : 'complete',
      defaultSlot: 'actionBar',
      mandatory: true,
      action: lifecycleAction,
    });
  }

  // 범용 조건(visibleWhen/enableWhen/primaryWhen) scope — web DRAFT 직후와 같은 값.
  const scope = {
    status: 'DRAFT',
    hasApprovalLine: hasApprovalLinePolicy,
    contactSignatureComplete: false,
    authorSigned: false,
    editedAfterRejection: false,
  };
  return resolveDetailChromeActions(tagged, detailChrome, 'DRAFT', { availableWidth, scope });
}
